/**
 * §P.1 · La ficha médica.
 *
 * **Categoría especial de datos** (RGPD art. 9), y eso cambia tres cosas:
 *
 * **Quién la ve.** El entrenador sí, porque es quien está delante cuando a un niño
 * le da una reacción alérgica. El asistente no. Lo decide `permisos.ts`, y el
 * servidor lo impide igual.
 *
 * **Se enseña plegada.** No porque sea un secreto para quien tiene permiso, sino
 * porque una alergia no tiene que estar en pantalla cuando el administrativo abre
 * una ficha para corregir un número de socio con la familia al lado del mostrador.
 * Un toque para verla, y quien la abre sabe lo que está abriendo.
 *
 * **Se dice quién la escribió.** La editan la familia y el club, así que cuando
 * algo está mal —«aquí dice que es alérgico al marisco y no lo es»— la primera
 * pregunta es de quién viene el dato.
 *
 * Y es un `PUT`: **el formulario se manda entero**. Con un `PATCH`, vaciar
 * «alergias» necesitaría un `null` explícito que la mitad de los clientes mandaría
 * como `''`.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  estaVacia,
  type FichaMedica as Ficha,
} from '@/club/alumnos/salud/tipos';

interface Campos {
  allergies: string;
  conditions: string;
  medication: string;
  blood_type: string;
  dietary_notes: string;
  doctor_phone: string;
}

const desde = (f: Ficha): Campos => ({
  allergies: f.allergies ?? '',
  conditions: f.conditions ?? '',
  medication: f.medication ?? '',
  blood_type: f.blood_type ?? '',
  dietary_notes: f.dietary_notes ?? '',
  doctor_phone: f.doctor_phone ?? '',
});

const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function FichaMedica({
  club,
  alumnoId,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [abierta, setAbierta] = useState(false);
  const [editando, setEditando] = useState(false);
  const [campos, setCampos] = useState<Campos | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const ficha = useQuery({
    queryKey: ['salud', club, alumnoId],
    // No se pide hasta que alguien la abre: es dato de salud de un menor y no se
    // trae «por si acaso» al pintar una ficha.
    enabled: abierta,
    queryFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .health.get();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Ficha;
    },
  });

  const guardar = useMutation({
    mutationFn: async (valores: Campos) => {
      // Los seis campos, siempre. Es un PUT: lo que no se manda se borra.
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .health.put({
          allergies: oNulo(valores.allergies),
          conditions: oNulo(valores.conditions),
          medication: oNulo(valores.medication),
          blood_type: oNulo(valores.blood_type),
          dietary_notes: oNulo(valores.dietary_notes),
          doctor_phone: oNulo(valores.doctor_phone),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Ficha;
    },
    onSuccess: () => {
      setProblema(null);
      setEditando(false);
      setCampos(null);
      void cache.invalidateQueries({ queryKey: ['salud', club, alumnoId] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (!abierta) {
    return (
      <div className="plegado">
        <div>
          <strong>Ficha médica y alergias</strong>
          <p className="tenue">
            Datos de salud. Se abren aparte a propósito: no tienen que estar en
            pantalla mientras haces otra cosa.
          </p>
        </div>
        <button type="button" onClick={() => setAbierta(true)}>
          Ver la ficha médica
        </button>
      </div>
    );
  }

  if (ficha.isPending)
    return <Cargando filas={3} etiqueta="Cargando la ficha médica" />;

  if (ficha.isError) {
    return (
      <PanelError
        problema={aProblema(ficha.error)}
        reintentar={() => void ficha.refetch()}
      />
    );
  }

  const actual = ficha.data;

  if (editando && campos) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate(campos);
        }}
      >
        <div className="rejilla">
          <label>
            Alergias
            <input
              value={campos.allergies}
              onChange={(e) =>
                setCampos({ ...campos, allergies: e.target.value })
              }
            />
          </label>
          <label>
            Patologías
            <input
              value={campos.conditions}
              onChange={(e) =>
                setCampos({ ...campos, conditions: e.target.value })
              }
            />
          </label>
          <label>
            Medicación
            <input
              value={campos.medication}
              onChange={(e) =>
                setCampos({ ...campos, medication: e.target.value })
              }
            />
          </label>
          <label>
            Grupo sanguíneo
            <input
              maxLength={8}
              placeholder="0+, A−…"
              value={campos.blood_type}
              onChange={(e) =>
                setCampos({ ...campos, blood_type: e.target.value })
              }
            />
          </label>
          <label>
            Dieta
            <input
              value={campos.dietary_notes}
              onChange={(e) =>
                setCampos({ ...campos, dietary_notes: e.target.value })
              }
            />
          </label>
          <label>
            Teléfono del médico
            <input
              value={campos.doctor_phone}
              onChange={(e) =>
                setCampos({ ...campos, doctor_phone: e.target.value })
              }
            />
          </label>
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar la ficha'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setEditando(false);
              setCampos(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="salud">
      {estaVacia(actual) ? (
        <p className="tenue">
          Nadie ha rellenado la ficha médica de este alumno. Es la que se mira
          cuando hay un problema en clase, así que conviene que no esté vacía.
        </p>
      ) : (
        <dl className="datos">
          <Dato titulo="Alergias" valor={actual.allergies} destacado />
          <Dato titulo="Patologías" valor={actual.conditions} />
          <Dato titulo="Medicación" valor={actual.medication} destacado />
          <Dato titulo="Grupo sanguíneo" valor={actual.blood_type} />
          <Dato titulo="Dieta" valor={actual.dietary_notes} />
          <Dato titulo="Teléfono del médico" valor={actual.doctor_phone} />
        </dl>
      )}

      <div className="salud-pie">
        {actual.updated_at && (
          // De quién viene el dato. Lo primero que se pregunta cuando algo no cuadra.
          <span className="tenue">
            Última vez:{' '}
            {actual.updated_by_name
              ? `${actual.updated_by_name}, ${fechaYHora(actual.updated_at)}`
              : fechaYHora(actual.updated_at)}
          </span>
        )}

        {puedeEditar && (
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setCampos(desde(actual));
              setEditando(true);
            }}
          >
            {estaVacia(actual) ? 'Rellenar la ficha' : 'Editar la ficha'}
          </button>
        )}

        <button
          type="button"
          className="btn-plano"
          onClick={() => setAbierta(false)}
        >
          Ocultar
        </button>
      </div>
    </div>
  );
}

/**
 * Un dato de la ficha.
 *
 * `destacado` es para alergias y medicación, que son las dos que importan **en el
 * momento**: el resto se consulta, esas dos se buscan con prisa.
 */
function Dato({
  titulo,
  valor,
  destacado = false,
}: {
  titulo: string;
  valor: string | null;
  destacado?: boolean;
}) {
  return (
    <div className={destacado && valor ? 'dato-destacado' : undefined}>
      <dt>{titulo}</dt>
      <dd>{valor ?? '—'}</dd>
    </div>
  );
}
